import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock, CheckCircle, XCircle, AlertTriangle, ArrowRight,
  RotateCcw, Award, BookOpen, Loader2, Shield, ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOPIC_COLORS = {
  Vedas: 'bg-purple-50 text-purple-700 border-purple-200',
  Mantras: 'bg-blue-50 text-blue-700 border-blue-200',
  'Pooja Vidhi': 'bg-amber-50 text-amber-700 border-amber-200',
  Sanskrit: 'bg-green-50 text-green-700 border-green-200',
  Dharma: 'bg-orange-50 text-orange-700 border-orange-200',
};

export default function PanditExamPage({ user }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('dharmbook_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [phase, setPhase] = useState('loading'); // loading, intro, exam, result, cooldown, passed
  const [status, setStatus] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState('');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  // Check exam status on load
  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const res = await axios.get(`${API}/pandit/exam/status`, { headers });
      setStatus(res.data);
      if (res.data.reason === 'already_passed') {
        setPhase('passed');
      } else if (res.data.reason === 'cooldown') {
        setPhase('cooldown');
      } else {
        setPhase('intro');
      }
    } catch {
      toast.error('Failed to check exam status');
      setPhase('intro');
    }
  };

  // Start exam
  const startExam = async () => {
    try {
      const res = await axios.post(`${API}/pandit/exam/start`, {}, { headers });
      setQuestions(res.data.questions);
      setAttemptId(res.data.attempt_id);
      setTimeLeft(res.data.duration_minutes * 60);
      setAnswers({});
      setCurrentQ(0);
      setPhase('exam');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start exam');
    }
  };

  // Timer
  useEffect(() => {
    if (phase !== 'exam' || timeLeft <= 0) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          submitExam();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // Submit exam
  const submitExam = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    try {
      const res = await axios.post(`${API}/pandit/exam/submit`, {
        attempt_id: attemptId,
        answers,
      }, { headers });
      setResult(res.data);
      setPhase('result');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  }, [attemptId, answers, submitting]);

  const selectAnswer = (qId, optionIndex) => {
    setAnswers(prev => ({ ...prev, [qId]: optionIndex }));
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const answeredCount = Object.keys(answers).length;

  // ==================== RENDER PHASES ====================

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  // INTRO PHASE
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="exam-intro-page">
        <div className="bg-gradient-to-br from-orange-600 via-orange-500 to-amber-500 text-white px-4 py-8 text-center">
          <div className="w-16 h-16 mx-auto bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4">
            <BookOpen className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold mb-1">DharmBook Certification</h1>
          <p className="text-orange-100 text-sm">Pandit Ji Knowledge Examination</p>
        </div>

        <div className="max-w-lg mx-auto px-4 -mt-4">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="font-bold text-slate-800 text-lg mb-4">Exam Details</h2>
            <div className="space-y-3 mb-6">
              {[
                { icon: '📝', label: '20 Multiple Choice Questions' },
                { icon: '⏱️', label: '15 Minutes Time Limit' },
                { icon: '🎯', label: '70% Passing Score (14/20)' },
                { icon: '📚', label: '5 Topics: Vedas, Mantras, Pooja Vidhi, Sanskrit, Dharma' },
                { icon: '🔄', label: 'Retake after 24 hours if failed' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-sm text-slate-700">
                  <span className="text-lg flex-shrink-0">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  Once started, the timer cannot be paused. Make sure you have 15 uninterrupted minutes.
                </p>
              </div>
            </div>

            <button
              onClick={startExam}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold rounded-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              data-testid="start-exam-btn"
            >
              Start Examination <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // COOLDOWN PHASE
  if (phase === 'cooldown') {
    const remaining = status?.cooldown_remaining_seconds || 0;
    const hours = Math.floor(remaining / 3600);
    const mins = Math.floor((remaining % 3600) / 60);
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4" data-testid="exam-cooldown">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto bg-amber-50 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="font-bold text-xl text-slate-800 mb-2">Cooldown Period</h2>
          <p className="text-slate-500 text-sm mb-4">
            Last score: {status?.last_percentage}% — You can retake after cooldown.
          </p>
          <div className="bg-amber-50 rounded-xl p-4 mb-6">
            <p className="text-2xl font-bold text-amber-700">{hours}h {mins}m remaining</p>
          </div>
          <button onClick={() => navigate('/pandit')} className="px-6 py-2.5 bg-orange-500 text-white rounded-xl font-semibold">
            Back to Pandit Ji
          </button>
        </div>
      </div>
    );
  }

  // ALREADY PASSED
  if (phase === 'passed') {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4" data-testid="exam-passed">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto bg-green-50 rounded-full flex items-center justify-center mb-4">
            <Award className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="font-bold text-xl text-slate-800 mb-1">DharmBook Certified!</h2>
          <p className="text-green-600 font-semibold text-sm mb-4">Score: {status?.score}%</p>
          <p className="text-slate-500 text-sm mb-6">You have passed the DharmBook Pandit Certification Exam.</p>
          <div className="space-y-2">
            <button onClick={() => navigate('/pandit/verify')} className="w-full py-2.5 bg-orange-500 text-white rounded-xl font-semibold">
              Continue Verification <ChevronRight className="w-4 h-4 inline" />
            </button>
            <button onClick={() => navigate('/pandit')} className="w-full py-2.5 text-slate-600 hover:bg-slate-50 rounded-xl font-medium">
              Back to Pandit Ji
            </button>
          </div>
        </div>
      </div>
    );
  }

  // EXAM PHASE
  if (phase === 'exam') {
    const q = questions[currentQ];
    const isLast = currentQ === questions.length - 1;
    const isLowTime = timeLeft <= 60;
    
    return (
      <div className="min-h-screen bg-stone-50" data-testid="exam-active">
        {/* Timer Bar */}
        <div className={`sticky top-0 z-40 px-4 py-2.5 flex items-center justify-between ${isLowTime ? 'bg-red-600' : 'bg-slate-800'} text-white`}>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span className={`font-mono font-bold text-lg ${isLowTime ? 'animate-pulse' : ''}`}>{formatTime(timeLeft)}</span>
          </div>
          <span className="text-xs font-medium">{answeredCount}/{questions.length} answered</span>
          <button
            onClick={submitExam}
            disabled={submitting}
            className="px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            data-testid="submit-exam-btn"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-4 py-3 bg-white border-b border-stone-100">
          <div className="flex gap-1 overflow-x-auto">
            {questions.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentQ(i)}
                className={`w-7 h-7 flex-shrink-0 rounded-lg text-[10px] font-bold transition-all ${
                  i === currentQ ? 'bg-orange-500 text-white scale-110' :
                  answers[questions[i]?.id] !== undefined ? 'bg-green-100 text-green-700 border border-green-300' :
                  'bg-stone-100 text-stone-500'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Question */}
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="mb-3 flex items-center justify-between">
            <span className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${TOPIC_COLORS[q.topic] || 'bg-stone-50 text-stone-600 border-stone-200'}`}>
              {q.topic}
            </span>
            <span className="text-xs text-slate-400">Q{currentQ + 1} of {questions.length}</span>
          </div>

          <h3 className="text-lg font-bold text-slate-800 mb-5 leading-relaxed" data-testid="exam-question">
            {q.question}
          </h3>

          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const isSelected = answers[q.id] === i;
              return (
                <button
                  key={i}
                  onClick={() => selectAnswer(q.id, i)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-orange-500 bg-orange-50 text-orange-800'
                      : 'border-stone-200 bg-white text-slate-700 hover:border-orange-300 hover:bg-orange-50/30'
                  }`}
                  data-testid={`option-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      isSelected ? 'border-orange-500 bg-orange-500 text-white' : 'border-slate-300 text-slate-500'
                    }`}>
                      {String.fromCharCode(65 + i)}
                    </div>
                    <span className="font-medium text-sm">{opt}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={() => setCurrentQ(c => Math.max(0, c - 1))}
              disabled={currentQ === 0}
              className="px-5 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-medium text-sm disabled:opacity-30 hover:bg-slate-50"
            >
              Previous
            </button>
            {isLast ? (
              <button
                onClick={submitExam}
                disabled={submitting}
                className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-sm flex items-center gap-2 disabled:opacity-50"
                data-testid="finish-exam-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Finish Exam
              </button>
            ) : (
              <button
                onClick={() => setCurrentQ(c => Math.min(questions.length - 1, c + 1))}
                className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium text-sm flex items-center gap-1"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // RESULT PHASE
  if (phase === 'result' && result) {
    return (
      <div className="min-h-screen bg-stone-50" data-testid="exam-result">
        <div className={`px-4 py-8 text-center ${result.passed ? 'bg-gradient-to-br from-green-600 to-emerald-600' : 'bg-gradient-to-br from-slate-600 to-slate-700'} text-white`}>
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${result.passed ? 'bg-white/20' : 'bg-white/10'}`}>
            {result.passed ? <Award className="w-10 h-10" /> : <RotateCcw className="w-10 h-10" />}
          </div>
          <h1 className="text-2xl font-bold mb-1">{result.passed ? 'Congratulations!' : 'Keep Studying!'}</h1>
          <p className="text-white/80 text-sm">{result.passed ? 'You passed the DharmBook Certification' : 'You can retake after 24 hours'}</p>
        </div>

        <div className="max-w-md mx-auto px-4 -mt-4">
          {/* Score Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
            <div className="text-center mb-4">
              <p className="text-5xl font-bold text-slate-800">{result.percentage}%</p>
              <p className="text-sm text-slate-500 mt-1">{result.score} / {result.total} correct</p>
              <p className={`text-xs mt-1 font-semibold ${result.passed ? 'text-green-600' : 'text-red-500'}`}>
                {result.passed ? 'PASSED' : `FAILED (Need ${result.passing_percentage}%)`}
              </p>
            </div>

            {/* Topic Breakdown */}
            <h3 className="font-semibold text-sm text-slate-700 mb-3">Topic Breakdown</h3>
            <div className="space-y-2">
              {result.topic_scores && Object.entries(result.topic_scores).map(([topic, scores]) => {
                const pct = Math.round((scores.correct / scores.total) * 100);
                return (
                  <div key={topic} className="flex items-center gap-3">
                    <span className="text-xs text-slate-600 w-24 flex-shrink-0">{topic}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct >= 70 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-slate-700 w-16 text-right">{scores.correct}/{scores.total}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            {result.passed ? (
              <button
                onClick={() => navigate('/pandit/verify')}
                className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold flex items-center justify-center gap-2"
              >
                <Shield className="w-5 h-5" /> Continue to Document Verification
              </button>
            ) : (
              <button disabled className="w-full py-3 bg-slate-200 text-slate-500 rounded-xl font-bold cursor-not-allowed">
                Retake Available in 24 Hours
              </button>
            )}
            <button onClick={() => navigate('/pandit')} className="w-full py-2.5 text-slate-600 hover:bg-slate-50 rounded-xl font-medium">
              Back to Pandit Ji
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
